"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import {
  buildConclusion,
  type ClosingType,
} from "@/lib/tool-kit/essay/conclusion";

const CLOSING_TYPES: { value: ClosingType }[] = [
  { value: "call-to-action" },
  { value: "implication" },
  { value: "future" },
];

const DOS_AND_DONTS_GROUPS: {
  headingKey: "doHeading" | "dontHeading";
  itemsKey: "doItems" | "dontItems";
}[] = [
  { headingKey: "doHeading", itemsKey: "doItems" },
  { headingKey: "dontHeading", itemsKey: "dontItems" },
];

/** English strings. A translated content file overrides these through `ui`. */
export const CONCLUSION_GENERATOR_STRINGS = {
  yourThesisRestateItIn: "Your thesis (restate it in fresh words)",
  eGSchoolUniformsReduce:
    "e.g. School uniforms reduce social pressure and help students focus on learning",
  tipRephraseYourOriginalThesis:
    "Tip: rephrase your original thesis rather than copying it word for word — the conclusion should echo it, not repeat it.",
  yourMainPoints23: "Your main points (2-3, one per box)",
  mainPoint1: "Main point 1",
  mainPoint1EG:
    "Main point 1 — e.g. uniforms lower the cost pressure on families",
  mainPoint2: "Main point 2",
  mainPoint2EG:
    "Main point 2 — e.g. they reduce visible status differences between students",
  mainPoint3Optional: "Main point 3 (optional)",
  closingStyle: "Closing style",
  buildConclusion: "Build Conclusion",
  copied: "Copied!",
  copy: "Copy",
  enterYourThesisAndAt:
    "Enter your thesis and at least two main points to build a draft.",
  yourConclusionDraftEditIt:
    "Your conclusion draft — edit it into your own voice and replace any bracketed placeholders",
  conclusionDoSAndDon: "Conclusion do's and don'ts",
  closingLabels: {
    "call-to-action": "Call to action",
    implication: "Broader implication",
    future: "Future outlook",
  },
  restateThesisFrame: "This essay has argued that {thesis}.",
  pointsFrame:
    "Taken together, {points} demonstrate the strength of this argument.",
  closingFrames: {
    "call-to-action":
      "It is now up to [readers, policymakers, researchers] to act on these findings.",
    implication:
      "These conclusions reach beyond this topic alone, shaping how we understand [the wider issue].",
    future:
      "As [the field or situation] continues to evolve, these insights will only grow more relevant.",
  },
  doHeading: "Do",
  dontHeading: "Don't",
  doItems: [
    "Restate your thesis in fresh words that reflect the evidence you presented.",
    "Synthesize your main points into a single takeaway rather than listing them again.",
    "End with a closing thought — a call to action, an implication, or a future outlook.",
    "Keep it proportional: roughly 5-10% of the essay's total length.",
  ],
  dontItems: [
    "Introduce new evidence, sources, or arguments the body never covered.",
    "Repeat your thesis or topic sentences word for word.",
    "Open with tired phrases like 'In summary, as I said above'.",
    "Apologize or undercut your argument ('This essay only scratched the surface...').",
  ],
} as const;

export default function ConclusionGeneratorWidget() {
  const [thesis, setThesis] = useState("");
  const [pointOne, setPointOne] = useState("");
  const [pointTwo, setPointTwo] = useState("");
  const [pointThree, setPointThree] = useState("");
  const [closing, setClosing] = useState<ClosingType>("implication");
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);
  const strings = useToolUi(CONCLUSION_GENERATOR_STRINGS);
  const locale = useToolLocale();

  const points = [pointOne, pointTwo, pointThree]
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  const canGenerate = thesis.trim().length > 0 && points.length >= 2;

  function handleGenerate() {
    if (!canGenerate) {
      return;
    }
    setOutput(
      buildConclusion(
        { thesis, points, closing },
        {
          restate: strings.restateThesisFrame,
          points: strings.pointsFrame,
          closing: strings.closingFrames,
        },
        locale
      )
    );
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Thesis */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="conclusion-thesis"
        >
          {strings.yourThesisRestateItIn}
        </label>
        <textarea
          className="min-h-[80px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="conclusion-thesis"
          onChange={(e) => setThesis(e.target.value)}
          placeholder={strings.eGSchoolUniformsReduce}
          value={thesis}
        />
        <p className="text-muted-foreground text-xs">
          {strings.tipRephraseYourOriginalThesis}
        </p>
      </div>
      {/* Main points */}
      <section className="space-y-4">
        <p className="font-medium text-foreground text-sm">
          {strings.yourMainPoints23}
        </p>
        <div className="space-y-3">
          <input
            aria-label={strings.mainPoint1}
            className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            onChange={(e) => setPointOne(e.target.value)}
            placeholder={strings.mainPoint1EG}
            type="text"
            value={pointOne}
          />
          <input
            aria-label={strings.mainPoint2}
            className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            onChange={(e) => setPointTwo(e.target.value)}
            placeholder={strings.mainPoint2EG}
            type="text"
            value={pointTwo}
          />
          <input
            aria-label={strings.mainPoint3Optional}
            className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            onChange={(e) => setPointThree(e.target.value)}
            placeholder={strings.mainPoint3Optional}
            type="text"
            value={pointThree}
          />
        </div>
      </section>
      {/* Closing type */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.closingStyle}
        </p>
        <div className="flex flex-wrap gap-3">
          {CLOSING_TYPES.map((t) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={t.value}
            >
              <input
                checked={closing === t.value}
                className="accent-primary"
                name="closing-type"
                onChange={() => setClosing(t.value)}
                type="radio"
                value={t.value}
              />
              {strings.closingLabels[t.value]}
            </label>
          ))}
        </div>
      </section>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={!canGenerate}
          onClick={handleGenerate}
          type="button"
        >
          {strings.buildConclusion}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copy}
        </button>
      </div>
      {!canGenerate && (
        <p className="text-muted-foreground text-xs">
          {strings.enterYourThesisAndAt}
        </p>
      )}
      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="conclusion-output"
          >
            {strings.yourConclusionDraftEditIt}
          </label>
          <textarea
            className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="conclusion-output"
            onChange={(e) => setOutput(e.target.value)}
            value={output}
          />
        </div>
      )}
      {/* Do's and Don'ts */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.conclusionDoSAndDon}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {DOS_AND_DONTS_GROUPS.map((group) => (
            <div
              className="rounded-lg border border-border bg-card p-5"
              key={group.headingKey}
            >
              <h3 className="font-medium text-foreground">
                {strings[group.headingKey]}
              </h3>
              <ul className="mt-2 list-disc space-y-2 pl-5 text-muted-foreground text-sm leading-relaxed">
                {strings[group.itemsKey].map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
